import axios from "axios";
import { useEffect, useState } from "react";

export default function MyAccount() {
   const [form, setForm] = useState({
      paymentMethod: "",
      paymentFrequency: "",
      paymentContact: "",
      ach_account_name: "",
      ach_account_number: "",
      ach_routing_number: "",
      ach_account_type: "",
      stripe_account_id: "",
   });

   const [loading, setLoading] = useState(true);
   const [message, setMessage] = useState("");

   // ✅ Fetch existing payment details from backend
   useEffect(() => {
      const fetchPaymentSettings = async () => {
         try {
            const driverId = sessionStorage.getItem("driver_id"); // 👈 from sessionStorage
            if (!driverId) {
               setMessage("❌ Driver not logged in.");
               setLoading(false);
               return;
            }

            const res = await axios.get(`/auth/getpayment/${driverId}`);
            if (res.data) {
               setForm((prev) => ({ ...prev, ...res.data }));
            }
         } catch (err) {
            console.error("Error fetching payment settings:", err);
         } finally {
            setLoading(false);
         }
      };
      fetchPaymentSettings();
   }, []);

   // ✅ Handle input changes
   const handleChange = (e) => {
      const { name, value } = e.target;
      setForm((prev) => ({ ...prev, [name]: value }));
   };

   // ✅ Submit form to backend
   const handleSubmit = async (e) => {
      e.preventDefault();
      setMessage("");
      try {
         const driverId = sessionStorage.getItem("driver_id"); // 👈 send with update
         await axios.post(`/auth/updatepayment/${driverId}`, form);
         setMessage("✅ Payment settings updated successfully!");
      } catch (err) {
         console.error("Error updating payment settings:", err);
         setMessage("❌ Failed to update settings. Please try again.");
      }
   };

   if (loading) {
      return (
         <section className="px-4 sm:px-6 lg:px-8 py-8 min-h-screen flex items-center justify-center bg-gray-50">
            <p className="text-gray-600">Loading payment settings...</p>
         </section>
      );
   }

   return (
      <section className="px-4 sm:px-6 lg:px-8 py-8 min-h-screen flex items-center justify-center bg-gray-50">
         <div className="w-full max-w-2xl bg-white rounded-2xl shadow p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6 text-center">
               Update Payment Settings
            </h2>

            {message && (
               <div
                  className={`mb-4 p-3 rounded text-sm text-center ${message.startsWith("✅")
                     ? "bg-green-100 text-green-800"
                     : "bg-red-100 text-red-800"
                     }`}
               >
                  {message}
               </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
               {/* Payment Method */}
               <div>
                  <label className="block text-sm font-medium mb-1">
                     How do you want to be paid?
                  </label>
                  <select
                     name="paymentMethod"
                     value={form.paymentMethod}
                     onChange={handleChange}
                     required
                     className="w-full p-2.5 rounded-md bg-green-50 border text-sm"
                  >
                     <option value="">Select Payment Method</option>
                     <option value="Cash">Cash</option>
                     <option value="ACH">ACH "Electronic Payment"</option>
                     <option value="Ev-voucher">EV charging voucher</option>
                     <option value="Fuel-Voucher">Fuel Voucher</option>
                     <option value="Stock-credit">Stock-credit</option>
                     <option value="PayPal">PayPal</option>
                     <option value="Check by Mail">Check by Mail</option>
                  </select>
                  <p className="text-sm text-gray-600 mt-1">
                     Service Fees: <strong>10%</strong> per payment{" "}
                     <em>(PayPal / Stripe fee)</em>
                  </p>
               </div>

               {/* Payment Frequency */}
               <div>
                  <label className="block text-sm font-medium mb-1">
                     Payment Frequency
                  </label>
                  <div className="flex items-center gap-6">
                     <label className="flex items-center text-sm">
                        <input
                           type="radio"
                           name="paymentFrequency"
                           value="Weekly"
                           checked={form.paymentFrequency === "Weekly"}
                           onChange={handleChange}
                           className="form-radio text-green-600"
                           required
                        />
                        <span className="ml-2">Weekly</span>
                     </label>
                     <label className="flex items-center text-sm">
                        <input
                           type="radio"
                           name="paymentFrequency"
                           value="Daily"
                           checked={form.paymentFrequency === "Daily"}
                           onChange={handleChange}
                           className="form-radio text-green-600"
                        />
                        <span className="ml-2">Daily</span>
                     </label>
                  </div>
               </div>

               {/* Conditional Fields */}
               {form.paymentMethod === "PayPal" && (
                  <div>
                     <label className="block text-sm font-medium mb-1">
                        PayPal Email
                     </label>
                     <input
                        type="email"
                        name="paymentContact"
                        value={form.paymentContact || ""}
                        onChange={handleChange}
                        placeholder="PayPal Email"
                        className="w-full p-2.5 rounded-md bg-green-50 border text-sm"
                        required
                     />
                  </div>
               )}

               {form.paymentMethod === "ACH" && (
                  <>
                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                           <label className="block text-sm font-medium mb-1">
                              ACH Account Name
                           </label>
                           <input
                              type="text"
                              name="ach_account_name"
                              value={form.ach_account_name || ""}
                              onChange={handleChange}
                              className="w-full p-2.5 rounded-md bg-green-50 border text-sm"
                              required
                           />
                        </div>
                        <div>
                           <label className="block text-sm font-medium mb-1">
                              ACH Account Number
                           </label>
                           <input
                              type="text"
                              name="ach_account_number"
                              value={form.ach_account_number || ""}
                              onChange={handleChange}
                              className="w-full p-2.5 rounded-md bg-green-50 border text-sm"
                              required
                           />
                        </div>
                     </div>

                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                           <label className="block text-sm font-medium mb-1">
                              ACH Routing Number
                           </label>
                           <input
                              type="text"
                              name="ach_routing_number"
                              value={form.ach_routing_number || ""}
                              onChange={handleChange}
                              className="w-full p-2.5 rounded-md bg-green-50 border text-sm"
                              required
                           />
                        </div>
                        <div>
                           <label className="block text-sm font-medium mb-1">
                              ACH Account Type
                           </label>
                           <select
                              name="ach_account_type"
                              value={form.ach_account_type || ""}
                              onChange={handleChange}
                              className="w-full p-2.5 rounded-md bg-green-50 border text-sm"
                              required
                           >
                              <option value="">Select Account Type</option>
                              <option value="checking">Checking</option>
                              <option value="savings">Savings</option>
                           </select>
                        </div>
                     </div>

                     <div>
                        <label className="block text-sm font-medium mb-1">
                           Stripe Account ID
                        </label>
                        <input
                           type="text"
                           name="stripe_account_id"
                           value={form.stripe_account_id || ""}
                           onChange={handleChange}
                           className="w-full p-2.5 rounded-md bg-green-50 border text-sm"
                           required
                        />
                     </div>
                  </>
               )}

               {/* Submit */}
               <div className="pt-4">
                  <button
                     type="submit"
                     className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-md shadow"
                  >
                     Save Payment Settings
                  </button>
               </div>
            </form>
         </div>
      </section>
   );
}
